import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NT, NI, NMButton, NMChip } from './NaomiTheme';
import { MDialog, MTextField } from './NaomiUi';
import {
  getCatalogueImages, getCatalogueCategories,
  getCatalogueUploadSasUrl, saveCatalogueMetadata,
  updateCatalogueImage, deleteCatalogueImage,
} from './services/CatalogueService';
import type { CatalogueImage } from './services/CatalogueService';

// ─── Trade type grouping ──────────────────────────────────────────────────────

interface TradeGroup {
  parent: string;
  children: string[];
}

export function buildTradeGroups(types: string[]): TradeGroup[] {
  const childSet = new Set<string>();
  const groups: TradeGroup[] = [];

  const sortedTypes = Array.from(types).sort();

  // Find parent/child pairs: "Greenhouse Commercial" is a child of "Greenhouse"
  for (const candidate of sortedTypes) {
    const children = sortedTypes.filter(t => t !== candidate && t.startsWith(candidate + ' '));
    if (children.length > 0) {
      groups.push({ parent: candidate, children });
      children.forEach(c => childSet.add(c));
    }
  }

  // Standalone types (no parent, no children)
  for (const t of sortedTypes) {
    if (!childSet.has(t) && !groups.some(g => g.parent === t)) {
      groups.push({ parent: t, children: [] });
    }
  }

  return groups.sort((a, b) => a.parent.localeCompare(b.parent));
}

/** Display label: "Greenhouse Commercial" → "Greenhouse - Commercial" when parent exists in the type list. */
function typeLabel(typeValue: string, allTypes: string[]): string {
  const parent = allTypes.find(p => p !== typeValue && typeValue.startsWith(p + ' '));
  return parent ? `${parent} - ${typeValue.slice(parent.length + 1)}` : typeValue;
}

// ─── IndeterminateCheckbox ────────────────────────────────────────────────────

function IndeterminateCheckbox({ checked, indeterminate, onChange, label, bold }: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (v: boolean) => void;
  label: string;
  bold?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none' }}>
      <input ref={ref} type="checkbox" checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ cursor: 'pointer', width: 14, height: 14, accentColor: NT.primary, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: bold ? 600 : 400, color: NT.ink }}>{label}</span>
    </label>
  );
}

// ─── TradeTypePicker ──────────────────────────────────────────────────────────

function TradeTypePicker({ groups, selected, onChange }: {
  groups: TradeGroup[];
  selected: string[];
  onChange: (types: string[]) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(groups.filter(g => g.children.length > 0).map(g => g.parent))
  );

  const toggleExpand = (parent: string) => {
    setExpanded(prev => {
      const s = new Set(Array.from(prev));
      s.has(parent) ? s.delete(parent) : s.add(parent);
      return s;
    });
  };

  const toggle = (value: string, checked: boolean) => {
    if (checked) onChange(Array.from(new Set(Array.from(selected).concat([value]))));
    else onChange(selected.filter(t => t !== value));
  };

  const toggleGroup = (group: TradeGroup, checked: boolean) => {
    if (group.children.length === 0) {
      toggle(group.parent, checked);
    } else {
      if (checked) onChange(Array.from(new Set(Array.from(selected).concat(group.children))));
      else onChange(selected.filter(t => !group.children.includes(t)));
    }
  };

  if (groups.length === 0) return (
    <div style={{ padding: '8px 12px', fontSize: 13, color: NT.ink4, border: `1px solid ${NT.line}`, borderRadius: 8 }}>
      No trade types configured.
    </div>
  );

  return (
    <div style={{ border: `1px solid ${NT.line}`, borderRadius: 8, overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
      {groups.map((group, gi) => {
        const hasChildren = group.children.length > 0;
        const isOpen = expanded.has(group.parent);
        const selChildren = group.children.filter(c => selected.includes(c));
        const parentChecked = hasChildren
          ? selChildren.length === group.children.length && group.children.length > 0
          : selected.includes(group.parent);
        const parentIndet = hasChildren && selChildren.length > 0 && selChildren.length < group.children.length;
        const isLast = gi === groups.length - 1;

        return (
          <div key={group.parent}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
              background: gi % 2 === 0 ? NT.surfaceAlt : NT.surface,
              borderBottom: (isOpen || !isLast) ? `1px solid ${NT.line}` : 'none',
            }}>
              <span style={{ width: 16, flexShrink: 0, display: 'flex', alignItems: 'center',
                color: NT.ink3, cursor: hasChildren ? 'pointer' : 'default' }}
                onClick={() => hasChildren && toggleExpand(group.parent)}>
                {hasChildren ? (isOpen ? <NI.Chev d="down" /> : <NI.Chev d="right" />) : null}
              </span>
              <IndeterminateCheckbox
                checked={parentChecked}
                indeterminate={parentIndet}
                onChange={v => toggleGroup(group, v)}
                label={group.parent}
                bold={hasChildren}
              />
            </div>
            {hasChildren && isOpen && group.children.map((child, ci) => (
              <div key={child} style={{
                padding: '8px 14px 8px 42px',
                background: NT.surface,
                borderBottom: (ci < group.children.length - 1 || !isLast) ? `1px solid ${NT.line}` : 'none',
              }}>
                <IndeterminateCheckbox
                  checked={selected.includes(child)}
                  onChange={v => toggle(child, v)}
                  label={typeLabel(child, groups.map(g => g.parent))}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── CatalogueCard ────────────────────────────────────────────────────────────

function CatalogueCard({ image: img, allTypes, onView, onEdit, onDelete }: {
  image: CatalogueImage;
  allTypes: string[];
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const thumb = img.thumbnailPreviewUrl || img.previewUrl;

  const chips = useMemo(() => {
    return (img.category || '').split(',').map(t => t.trim()).filter(Boolean)
      .map(t => typeLabel(t, allTypes));
  }, [img.category, allTypes]);

  return (
    <div style={{ background: NT.surface, border: `1px solid ${hover ? NT.line2 : NT.line}`,
      borderRadius: 10, overflow: 'hidden', transition: 'all .12s',
      boxShadow: hover ? NT.shadow2 : 'none' }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div style={{ width: '100%', height: 140, background: NT.surfaceAlt,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {thumb
          ? <img src={thumb} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ opacity: 0.3, transform: 'scale(2)', display: 'inline-flex' }}><NI.Camera /></div>
        }
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: NT.ink, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 6 }} title={img.name}>
          {img.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: chips.length > 0 ? 4 : 0 }}>
              {chips.slice(0, 2).map((label, i) => (
                <NMChip key={i} color="neutral"
                  style={{ fontSize: 10.5, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {label}
                </NMChip>
              ))}
              {chips.length > 2 && <NMChip color="neutral" style={{ fontSize: 10.5 }}>+{chips.length - 2}</NMChip>}
            </div>
          </div>
          <div style={{ display: 'flex', flexShrink: 0, gap: 2 }}>
            <button onClick={onView} title="View"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: NT.ink4, padding: 4, borderRadius: 4, display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.color = NT.primary; e.currentTarget.style.background = NT.primarySoft; }}
              onMouseLeave={e => { e.currentTarget.style.color = NT.ink4; e.currentTarget.style.background = 'transparent'; }}>
              <NI.Eye />
            </button>
            <button onClick={onEdit} title="Edit"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: NT.ink4, padding: 4, borderRadius: 4, display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.color = NT.primary; e.currentTarget.style.background = NT.primarySoft; }}
              onMouseLeave={e => { e.currentTarget.style.color = NT.ink4; e.currentTarget.style.background = 'transparent'; }}>
              <NI.Edit />
            </button>
            <button onClick={onDelete} title="Delete"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: NT.ink4, padding: 4, borderRadius: 4, display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.color = NT.danger; e.currentTarget.style.background = NT.dangerSoft; }}
              onMouseLeave={e => { e.currentTarget.style.color = NT.ink4; e.currentTarget.style.background = 'transparent'; }}>
              <NI.Trash />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SidebarRow ──────────────────────────────────────────────────────────────

function SidebarRow({ label, count, selected, dimmed, onClick }: {
  label: string;
  count: number;
  selected: boolean;
  dimmed?: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
        background: selected ? NT.primarySoft : hover ? NT.surfaceAlt : 'transparent',
        color: selected ? NT.primary : dimmed ? NT.ink4 : NT.ink,
        opacity: dimmed ? 0.55 : 1,
        transition: 'background 0.1s',
      }}>
      <span style={{ fontSize: 12, fontWeight: selected ? 600 : 400,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {label}
      </span>
      <span style={{ fontSize: 10.5, color: selected ? NT.primary : NT.ink4,
        background: NT.surfaceAlt, borderRadius: 10,
        padding: '0 5px', marginLeft: 4, flexShrink: 0 }}>
        {count}
      </span>
    </div>
  );
}

// ─── Upload helpers ──────────────────────────────────────────────────────────

function createThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      const maxDim = 200;
      let w = img.width, h = img.height;
      if (w > h) { if (w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; } }
      else        { if (h > maxDim) { w = Math.round(w * maxDim / h); h = maxDim; } }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject('No canvas context'); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => blob ? resolve(blob) : reject('Canvas toBlob failed'), 'image/jpeg', 0.8);
    };
    img.onerror = reject;
    img.src = objUrl;
  });
}

function xhrPut(
  sasUrl: string,
  data: Blob | File,
  contentType: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100));
      };
    }
    xhr.onload = () => (xhr.status === 200 || xhr.status === 201) ? resolve() : reject(`HTTP ${xhr.status}`);
    xhr.onerror = () => reject('Network error');
    xhr.open('PUT', sasUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(data);
  });
}

// ─── CataloguePane ────────────────────────────────────────────────────────────

export function CataloguePane({ accessToken }: { accessToken: string }) {
  const [images,     setImages]     = useState<CatalogueImage[]>([]);
  const [tradeTypes, setTradeTypes] = useState<string[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [uploadProgress, setUploadProgress] = useState(-1); // -1 = idle, 0-100 = uploading
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');

  const [pendingFile,  setPendingFile]  = useState<File | null>(null);
  const [uploadName,   setUploadName]   = useState('');
  const [uploadCats,   setUploadCats]   = useState<string[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);

  const [editTarget,   setEditTarget]   = useState<CatalogueImage | null>(null);
  const [editName,     setEditName]     = useState('');
  const [editCats,     setEditCats]     = useState<string[]>([]);
  const [updating,     setUpdating]     = useState(false);

  const [viewTarget, setViewTarget] = useState<CatalogueImage | null>(null);
  const [toast,      setToast]      = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    setLoading(true);
    try { setImages(await getCatalogueImages(accessToken)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getCatalogueCategories(accessToken).then(setTradeTypes);
  }, [accessToken]);

  const tradeGroups = useMemo(() => buildTradeGroups(tradeTypes), [tradeTypes]);

  // Images matching search only — used for sidebar counts
  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return images;
    return images.filter(img =>
      img.name.toLowerCase().includes(q) || (img.category || '').toLowerCase().includes(q)
    );
  }, [images, search]);

  const countFor = (type: string) =>
    searchFiltered.filter(img =>
      (img.category || '').split(',').map(s => s.trim()).includes(type)
    ).length;

  const filtered = useMemo(() => searchFiltered.filter(img => {
    if (!catFilter) return true;
    return (img.category || '').split(',').map(s => s.trim()).includes(catFilter);
  }), [searchFiltered, catFilter]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setPendingFile(file); setUploadName(file.name); setUploadCats([]); }
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    const file     = pendingFile;
    const name     = uploadName.trim() || file.name;
    const category = uploadCats.length > 0 ? uploadCats.join(',') : null;
    const primaryCat = uploadCats[0] || 'uncategorized';
    const blobPath = `${primaryCat}/${name}`;
    let thumbnailPath: string | null = null;

    setUploadProgress(0);
    try {
      // Generate and upload thumbnail for images
      if (file.type.startsWith('image/')) {
        try {
          const thumbBlob = await createThumbnail(file);
          thumbnailPath   = `${primaryCat}/tn_${name}`;
          const thumbSas  = await getCatalogueUploadSasUrl(thumbnailPath, accessToken);
          if (thumbSas) await xhrPut(thumbSas, thumbBlob, 'image/jpeg');
        } catch {
          thumbnailPath = null;
        }
      }

      // Get write SAS URL for the main file
      const sasUrl = await getCatalogueUploadSasUrl(blobPath, accessToken);
      if (!sasUrl) throw new Error('Failed to get upload URL');

      // PUT file directly to Azure with live progress
      await xhrPut(sasUrl, file, file.type, pct => setUploadProgress(pct));

      // Save metadata to DB
      const result = await saveCatalogueMetadata(
        { name, imageUrl: blobPath, thumbnailUrl: thumbnailPath, category, contentType: file.type },
        accessToken,
      );
      setPendingFile(null); setUploadName(''); setUploadCats([]);
      if (result) { showToast('success', 'Image uploaded successfully!'); load(); }
      else { showToast('error', 'Upload failed. Please try again.'); }
    } catch {
      showToast('error', 'Upload failed. Please try again.');
    } finally {
      setUploadProgress(-1);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const ok = await deleteCatalogueImage(deleteTarget.id, accessToken);
    setDeleteTarget(null);
    if (ok) { showToast('success', 'Image removed from catalogue.'); load(); }
    else { showToast('error', 'Delete failed.'); }
  };

  const getFileExt = (filename: string): string => {
    const dot = (filename || '').lastIndexOf('.');
    return dot > 0 ? filename.slice(dot) : '';
  };

  const openEdit = (img: CatalogueImage) => {
    setEditTarget(img);
    const ext = getFileExt(img.imageUrl || '');
    const name = img.name || '';
    setEditName(name.toLowerCase().endsWith(ext.toLowerCase()) ? name.slice(0, name.length - ext.length) : name);
    setEditCats((img.category || '').split(',').map(s => s.trim()).filter(Boolean));
  };

  const handleUpdate = async () => {
    if (!editTarget || !editName.trim()) return;
    setUpdating(true);
    const ext = getFileExt(editTarget.imageUrl || '');
    const finalName = editName.trim() + ext;
    try {
      const result = await updateCatalogueImage(editTarget.id, finalName, editCats.join(','), accessToken);
      if (result) {
        setImages(prev => prev.map(img => img.id === editTarget.id
          ? { ...img, name: finalName, category: editCats.join(',') || null }
          : img));
        setEditTarget(null);
        showToast('success', 'Image updated successfully!');
        load();
      } else {
        showToast('error', 'Update failed. Please try again.');
      }
    } catch {
      showToast('error', 'Update failed. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const SelectedSummary = ({ cats }: { cats: string[] }) => {
    if (cats.length === 0) return (
      <div style={{ fontSize: 12, color: NT.ink4, marginBottom: 6, fontStyle: 'italic' }}>None selected</div>
    );
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {cats.map((t, i) => (
          <NMChip key={i} color="info">{typeLabel(t, tradeTypes)}</NMChip>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* ── Category sidebar ── */}
      {tradeTypes.length > 0 && (
        <div style={{
          width: 200, flexShrink: 0, borderRight: `1px solid ${NT.line}`,
          overflowY: 'auto', padding: '16px 8px', background: NT.surface,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: NT.ink4, textTransform: 'uppercase',
            letterSpacing: 0.8, padding: '0 4px 8px' }}>Categories</div>
          <SidebarRow label="All Images" count={searchFiltered.length}
            selected={catFilter === ''} onClick={() => setCatFilter('')} />
          <div style={{ height: 1, background: NT.line, margin: '6px 4px' }} />
          {tradeTypes.map(type => (
            <SidebarRow key={type}
              label={typeLabel(type, tradeTypes)}
              count={countFor(type)}
              selected={catFilter === type}
              dimmed={countFor(type) === 0}
              onClick={() => setCatFilter(prev => prev === type ? '' : type)}
            />
          ))}
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, padding: 24, overflow: 'auto' }} className="naomi-scroll">
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: NT.ink }}>Image catalogue</div>
            <div style={{ fontSize: 13, color: NT.ink3, marginTop: 2 }}>
              {loading ? 'Loading…' : `${images.length} image${images.length !== 1 ? 's' : ''} stored in Azure Blob`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 200 }}>
              <MTextField id="naomi-catalogue-search" placeholder="Search images…" value={search} onChange={setSearch} size="sm" adornment={<NI.Search />} />
            </div>
            <input type="file" ref={fileRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />
            <NMButton id="naomi-catalogue-upload-btn" color="primary" icon={<NI.Camera />}
              onClick={() => fileRef.current?.click()}>Upload image</NMButton>
          </div>
        </div>

        {/* Upload dialog */}
        <MDialog open={pendingFile !== null} title="Upload image"
          onClose={() => { setPendingFile(null); setUploadName(''); setUploadCats([]); }}
          actions={<>
            <NMButton variant="text" color="neutral" onClick={() => { setPendingFile(null); setUploadName(''); setUploadCats([]); }}>
              Cancel
            </NMButton>
            <NMButton color="primary" onClick={handleUpload} disabled={uploadProgress >= 0 || !uploadName.trim()}>
              {uploadProgress >= 0 ? `Uploading ${uploadProgress}%…` : 'Upload'}
            </NMButton>
          </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 380 }}>
            <div style={{ fontSize: 11.5, color: NT.ink4 }}>
              <strong>{uploadName || pendingFile?.name}</strong>
              {pendingFile && ` · ${(pendingFile.size / 1024).toFixed(1)} KB`}
            </div>
            <MTextField id="naomi-catalogue-upload-name" label="Name" value={uploadName}
              onChange={setUploadName} placeholder="Enter image name" size="sm" />
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                Trade categories
              </div>
              <SelectedSummary cats={uploadCats} />
              <TradeTypePicker groups={tradeGroups} selected={uploadCats} onChange={setUploadCats} />
            </div>
          </div>
        </MDialog>

        {/* Delete dialog */}
        <MDialog open={deleteTarget !== null} title="Remove image" onClose={() => setDeleteTarget(null)}
          actions={<>
            <NMButton variant="text" color="neutral" onClick={() => setDeleteTarget(null)}>Cancel</NMButton>
            <NMButton color="danger" onClick={handleDelete}>Remove</NMButton>
          </>}>
          <div style={{ fontSize: 13, color: NT.ink2, lineHeight: 1.6 }}>
            <strong>{deleteTarget?.name}</strong> will be removed from the catalogue.<br />
            The image file stays in Azure Blob Storage and can be restored by an administrator.
          </div>
        </MDialog>

        {/* Edit dialog */}
        {(() => {
          const editExt = getFileExt(editTarget?.imageUrl || '');
          return (
            <MDialog open={editTarget !== null} title="Edit image" onClose={() => setEditTarget(null)}
              actions={<>
                <NMButton variant="text" color="neutral" onClick={() => setEditTarget(null)}>Cancel</NMButton>
                <NMButton color="primary" onClick={handleUpdate} disabled={updating || !editName.trim()}>
                  {updating ? 'Saving…' : 'Save'}
                </NMButton>
              </>}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 380 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                  {(editTarget?.thumbnailPreviewUrl || editTarget?.previewUrl) && (
                    <img src={editTarget.thumbnailPreviewUrl || editTarget.previewUrl || ''} alt=""
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: `1px solid ${NT.line}`, flexShrink: 0 }} />
                  )}
                  <div style={{ fontSize: 12, fontWeight: 600, color: NT.ink2, wordBreak: 'break-all' }}>
                    {editName.trim() ? editName.trim() + editExt : (editTarget?.imageUrl || '')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Name</div>
                  <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${NT.line2}`, borderRadius: 6, overflow: 'hidden', background: NT.surface, height: 38 }}>
                    <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Enter image name"
                      style={{ flex: 1, border: 'none', outline: 'none', padding: '0 10px', background: 'transparent', fontSize: 13.5, color: NT.ink, fontFamily: 'inherit' }} />
                    {editExt && (
                      <span style={{ padding: '0 10px', fontSize: 13.5, color: NT.ink3, background: NT.surfaceAlt, borderLeft: `1px solid ${NT.line2}`, height: '100%', display: 'inline-flex', alignItems: 'center', fontWeight: 600, flexShrink: 0 }}>
                        {editExt}
                      </span>
                    )}
                  </div>
                  {editName.trim() && (
                    <div style={{ fontSize: 11, color: NT.ink4, marginTop: 4 }}>
                      Will save as: <strong>{editName.trim()}{editExt}</strong>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    Trade categories
                  </div>
                  <SelectedSummary cats={editCats} />
                  <TradeTypePicker groups={tradeGroups} selected={editCats} onChange={setEditCats} />
                </div>
              </div>
            </MDialog>
          );
        })()}

        {/* View dialog */}
        <MDialog open={viewTarget !== null} title={viewTarget?.name || 'Image'} onClose={() => setViewTarget(null)} width={720}
          actions={<NMButton variant="text" color="neutral" onClick={() => setViewTarget(null)}>Close</NMButton>}>
          {viewTarget && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: NT.surfaceAlt, borderRadius: 8, overflow: 'hidden', minHeight: 200 }}>
                <img src={viewTarget.previewUrl || ''} alt={viewTarget.name}
                  style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain', display: 'block' }} />
              </div>
              {viewTarget.category && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {viewTarget.category.split(',').map((t, i) => (
                    <NMChip key={i} color="info">{typeLabel(t.trim(), tradeTypes)}</NMChip>
                  ))}
                </div>
              )}
            </div>
          )}
        </MDialog>

        {/* Grid */}
        {loading && images.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: NT.ink4, fontSize: 13 }}>Loading…</div>
        ) : !loading && images.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: NT.ink4 }}>
            <div style={{ marginBottom: 16, opacity: 0.25, display: 'inline-flex', transform: 'scale(3)' }}><NI.Camera /></div>
            <div style={{ fontSize: 14, color: NT.ink3, marginTop: 12 }}>No images yet. Upload one using the button above.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: NT.ink4, fontSize: 13 }}>No images match your search.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            {filtered.map(img => (
              <CatalogueCard key={img.id} image={img} allTypes={tradeTypes}
                onView={() => setViewTarget(img)}
                onEdit={() => openEdit(img)}
                onDelete={() => setDeleteTarget({ id: img.id, name: img.name })} />
            ))}
          </div>
        )}
        </div>{/* maxWidth wrapper */}

      {toast && (
        <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'success' ? NT.success : NT.danger,
          color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8, zIndex: 9999, boxShadow: NT.shadow3,
          pointerEvents: 'none' }}>
          {toast.type === 'success' ? <NI.Check /> : <NI.Warn />}
          {toast.msg}
        </div>
      )}
      </div>{/* main content */}
    </div>
  );
}
